import { css, cx } from "@emotion/css";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEventListener } from "usehooks-ts";

import { ButtonGroup } from "../../button/ButtonGroup";
import { GhostIconButton } from "../../button/GhostIconButton";
import { useLayout } from "../../../foundations/grid-layout/Layout";

import { NavigationContext } from "../../../context/NavigationProvider";
import { mq, useBoxShadowBorder } from "../../utils";
interface Props {
  children: React.ReactNode;
  toolbar: React.ReactNode;
  isCollapsable?: boolean;
  width?: "standard" | "wide" | "full";
}

export function NavigationSidebar(props: Props) {
  const { t } = useTranslation();
  const { width = "standard" } = props;

  const ref = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const boxShadowBottomBorder = useBoxShadowBorder({ type: "bottom" });
  const { isCollapsed, setCollapsed } = useContext(NavigationContext);

  const { toggleMenu } = useLayout();

  useEventListener("scroll", (e) => {
    const target = e.currentTarget as Window;

    setIsScrolling(target.scrollY > 0);
  });

  const handlePress = () => {
    setCollapsed(!isCollapsed);
  };

  return (
    <nav
      ref={ref}
      className={css(styles.container, styles[width])}
      data-collapsed={isCollapsed}
    >
      {props.isCollapsable && (
        <div className={css(styles.collapserContainer)}>
          <div className={cx("collapser", styles.collapser)}>
            <GhostIconButton
              onPress={handlePress}
              hasDropShadow
              icon="keyboard_double_arrow_left"
              shape="border-radius-half"
              fill="background-active"
              hoverFill="background-active"
              size="small"
            />
          </div>
        </div>
      )}
      <header
        className={css(
          styles.header,
          isScrolling ? boxShadowBottomBorder : undefined
        )}
      >
        <ButtonGroup>{props.toolbar}</ButtonGroup>

        <GhostIconButton
          icon="close"
          onPress={() => toggleMenu(false)}
          testID="navigation-sidebar-close-button"
          aria-label={t("Close navigation sidebar")}
        />
      </header>
      <section className={cx("children", styles.children)}>
        {props.children}
      </section>
    </nav>
  );
}

const styles = {
  standard: css(
    mq({
      width: ["100%", "100%", "max-content"],
      minWidth: ["100%", "100%", 240],
      maxWidth: ["100%", "100%", "none"],
    })
  ),
  wide: css(
    mq({
      width: ["100%", "100%", "max-content"],
      minWidth: ["100%", "100%", 320],
      maxWidth: ["100%", "100%", "none"],
    })
  ),
  full: css({
    width: "100%",
  }),
  collapserContainer: css({
    position: "relative",
    width: "0px",
    height: "0px",
    marginLeft: "auto",
  }),
  collapser: css(
    {
      position: "absolute",
      left: "-15px",
      top: "92px",

      i: { transition: "transform 200ms ease" },
      transition: "opacity 200ms ease",
    },
    mq({
      display: ["none", "none", "flex"],
    })
  ),
  container: css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    transition: "width 200ms ease",

    ".collapser": {
      opacity: 0,
    },

    "&:hover .collapser": {
      opacity: 1,
    },

    "&[data-collapsed=true]": {
      ".collapser i": {
        transform: "rotate(180deg) !important",
      },

      width: 64,
      minWidth: 64,
      maxWidth: 64,
    },
  }),
  header: css(
    {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      height: "64px",
      paddingLeft: "var(--space-4)",
      paddingRight: "var(--space-4)",
    },
    mq({
      display: ["flex", "flex", "none"],
    })
  ),
  children: css(
    {
      flex: 1,
      height: "100%",
      minHeight: 0,
      paddingTop: "var(--space-4)",
      paddingBottom: "var(--space-16)",
      gap: "var(--space-1-5)",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
      overflowX: "hidden",

      "&::-webkit-scrollbar": {
        width: 3,
      },
      "&::-webkit-scrollbar-track": {
        opacity: 0,
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#D9D9D9",
        borderRadius: 24,
      },
    },
    mq({
      paddingBottom: ["130px", "130px", "var(--space-16)"],
    })
  ),
};
